import React from 'react';
import moment, { Moment } from 'moment';
import Collapse from 'components/collapse/collapse';
import { ChildTask, Customer, Skill, Tag, Task, Team } from '@bringg/types';
import AssignComponent, {
	Translations as AssignComponentTranslations
} from 'components/assign-component/assign-component';
import { default as _isNil } from 'lodash/isNil';
import { default as _map } from 'lodash/map';
import { default as _find } from 'lodash/find';
import { default as _first } from 'lodash/first';
import { default as _noop } from 'lodash/noop';
import TaskStatusInfo from 'components/task-status-info/task-status-info';
import { TaskStatusTranslations } from 'components/task-status/task-status';
import LinkedTasks, { Translations as LinkedTasksTranslations } from 'components/linked-tasks/linked-tasks';
import MultiTagsSelect from 'components/multi-tags-select/multi-tags-select';
import Label from 'components/label/label';
import TaskPromisedTimeWindow, {
	PromisedTimeWindow
} from 'components/task-promised-time-window/task-promised-time-window';
import DateDisplay, { getDateFormat } from 'components/date-display/date-display';
import Timezone from 'components/timezone/timezone';
import StarRating from 'components/star-rating/star-rating';
import { OrderPageConfiguration } from 'features/waypoint-card/waypoint-card.consts';

export interface Translations extends TaskStatusTranslations, AssignComponentTranslations, LinkedTasksTranslations {
	generalInfo: string;
	status: string;
	startedAt: string;
	team: string;
	tags: string;
	customer: string;
	linkedOrders: string;
	childOrders: string;
	skills: string;
	selectTag: string;
	selectSkills: string;
	promisedTime: string;
	servicePlan: string;
	parentTaskId: string;
	rating: string;
	createdAt: string;
}

interface TaskGeneralInfoProps {
	task: Task;
	customer?: Customer;
	linkedTask?: Task;
	childTasks?: ChildTask[];
	timezone?: string;
	promisedTimeWindow?: PromisedTimeWindow;
	hourFormat: string;
	teams?: Team[];
	renderTeamOption?: (team: Team) => React.ReactNode;
	tags?: Tag[];
	skills?: Skill[];
	onAssignedTeam?: (teamId: number) => void;
	onSkillsChanged?: (skills: string[]) => void;
	translations: Translations;
	onParentClick?: () => void;
	enableSkills?: boolean;
	editable?: boolean;
	configuration?: OrderPageConfiguration;
	onStatusClick?: () => void;
	statusHoverText?: string;
	rating?: Rating;
	navigateToTask: (taskId: number) => void;
	isTaskActive?: boolean;
}

export interface Rating {
	rating: number;
	created_at: string | Moment;
}

const getGeneralInfoItem = (shouldShow: boolean, title: string, content: React.ReactNode, testId: string) => {
	if (!shouldShow) {
		return;
	}

	return (
		<div className="general-info-item" data-test-id={testId}>
			<div className="general-info-item-title">{title}</div>

			{content}
		</div>
	);
};

const TaskGeneralInfo: React.FC<TaskGeneralInfoProps> = ({
	task,
	customer,
	linkedTask,
	childTasks,
	timezone,
	promisedTimeWindow,
	hourFormat = 'LT',
	teams,
	renderTeamOption,
	tags,
	skills,
	onAssignedTeam,
	onSkillsChanged,
	translations,
	onParentClick = _noop,
	enableSkills,
	editable = true,
	configuration,
	onStatusClick,
	statusHoverText,
	rating,
	navigateToTask,
	isTaskActive
}) => {
	const skillsValues = _map(skills, 'name');
	const selectedTag = _find(tags, tag => tag.id === task.tag_id);
	const { promisedStartTime, promisedEndTime } = promisedTimeWindow || {};
	const timeZoneForDates = configuration?.flags?.useTeamTimezone ? timezone : null;

	return (
		<div className="task-general-info">
			<Collapse testId={'general-info'} defaultActiveKey="task-general-info-panel">
				<Collapse.Panel key="task-general-info-panel" header={translations.generalInfo}>
					{getGeneralInfoItem(
						!_isNil(promisedStartTime) && !_isNil(promisedEndTime),
						translations.promisedTime,
						<TaskPromisedTimeWindow
							hourFormat={hourFormat}
							promisedTimeWindow={promisedTimeWindow}
							timezone={timezone}
							useTeamTimezone={configuration?.flags?.useTeamTimezone}
						/>,
						'promised-time'
					)}

					{getGeneralInfoItem(
						!_isNil(task.service_plan),
						translations.servicePlan,
						<Label className="task-general-info-label" text={task.service_plan?.name} />,
						'service-plan'
					)}

					{getGeneralInfoItem(
						!_isNil(task.status),
						translations.status,
						<TaskStatusInfo
							hoverText={statusHoverText}
							onClick={onStatusClick}
							task={task}
							dateFormat={getDateFormat(hourFormat)}
							translations={translations}
							timezone={timeZoneForDates}
						/>,
						'status-info'
					)}

					{getGeneralInfoItem(
						!_isNil(task.started_time),
						translations.startedAt,
						<div>
							<span className="task-general-info-date">
								<DateDisplay
									date={task.started_time}
									format={getDateFormat(hourFormat)}
									timezone={timeZoneForDates}
								/>
							</span>

							<Timezone timezone={timezone} />
						</div>,
						'date'
					)}

					{getGeneralInfoItem(
						!_isNil(task.parent_task_id),
						translations.parentTaskId,
						<div>
							<span className="task-general-info-parent-task-id" onClick={onParentClick}>
								{`#${task.parent_task_id}`}
							</span>
						</div>,
						'parent-task'
					)}

					{getGeneralInfoItem(
						!_isNil(teams),
						translations.team,
						<AssignComponent
							items={teams}
							renderOption={renderTeamOption}
							disabled={!isTaskActive || !editable}
							enableUnassign={false}
							selectedItemId={_first(task.team_ids)}
							onSelectedItem={onAssignedTeam}
							translations={translations}
						/>,
						'team'
					)}

					{getGeneralInfoItem(
						!_isNil(task.tag_id),
						translations.tags,
						<Label className="task-general-info-label" text={selectedTag?.tag} />,
						'tags'
					)}

					{getGeneralInfoItem(
						!!customer?.name,
						translations.customer,
						<span className="customer-info">{customer?.name}</span>,
						'customer'
					)}

					{getGeneralInfoItem(
						!!(childTasks || linkedTask),
						childTasks ? translations.childOrders : translations.linkedOrders,
						<LinkedTasks
							// TODO: resolve Task-ChildTask type conflicts or change LinkedTasks.task prop type
							tasks={(childTasks as Task[]) || [linkedTask]}
							translations={translations}
							navigateToTask={navigateToTask}
						/>,
						'linked-tasks'
					)}

					{getGeneralInfoItem(
						enableSkills,
						translations.skills,
						<MultiTagsSelect
							disabled={!isTaskActive || !editable}
							placeholder={translations.selectSkills}
							defaultValue={task.required_skills}
							tags={skillsValues}
							onTagsChange={onSkillsChanged}
						/>,
						'multi-tags'
					)}
					{getGeneralInfoItem(
						!_isNil(rating),
						translations.rating,
						<>
							<StarRating rating={rating?.rating} />
							<div>{`${translations.createdAt}: ${moment(rating?.created_at).calendar()}`}</div>
						</>,
						'rating'
					)}
				</Collapse.Panel>
			</Collapse>
		</div>
	);
};

export default TaskGeneralInfo;
